/**
 * Detect audio-hostile browser environments.
 *
 * The immersive prayer overlay tries to autoplay narration audio right
 * after a 3-2-1 countdown. Several environments *reliably* break this:
 *
 *   • Social-media in-app WebViews — Instagram, Facebook, Messenger,
 *     Twitter/X, LinkedIn, WhatsApp, WeChat, TikTok, Line. Autoplay is
 *     policy-blocked or the audio track is silently muted by the host
 *     app, so `audio.play()` may resolve but produce no sound at all.
 *   • Data-saver mode (Chrome / Samsung Internet) — the browser strips
 *     media element event dispatch, causing `waitForAudioReady` to hang
 *     until it times out.
 *
 * The prayer link is most likely to land in one of these environments
 * (they're where "share to X" sends the recipient). So instead of
 * fighting policies we can't win, we detect the environment BEFORE
 * the countdown, force silent-highlight mode, and show a friendly hint
 * with a "open in browser" affordance. Downstream code doesn't need to
 * know anything about the detection — it just reads `narrationMode`
 * which resolves to `'highlight_only'` when `audioBlocked` is true.
 *
 * IMPORTANT: this function is intentionally conservative. False
 * positives cost us the audio experience for someone who could have
 * enjoyed it; false negatives cost them a broken overlay AND a
 * confusing "the prayer is silent" moment. When in doubt we return
 * `hostile: false` and let the fallback path (part 2, in WatchPrayer)
 * catch any actual `play()` rejection at runtime.
 */

// Match order matters — most specific first so the reported reason is
// the friendliest possible ("Instagram" beats "generic WebView").
const IN_APP_WEBVIEW_MATCHERS = [
  { re: /\bInstagram\b/i,                            label: 'Instagram' },
  { re: /\bFBAN\b|\bFBAV\b|\bFB_IAB\b/i,             label: 'Facebook' },
  { re: /\bMessenger\b/i,                            label: 'Messenger' },
  { re: /\bTwitter\b|\bTwitterAndroid\b/i,           label: 'Twitter / X' },
  { re: /\bLinkedInApp\b/i,                          label: 'LinkedIn' },
  { re: /\bWhatsApp\b/i,                             label: 'WhatsApp' },
  { re: /\bMicroMessenger\b/i,                       label: 'WeChat' },
  { re: /\bTikTok\b|\bmusical_ly\b|\bByteLocale\b/i, label: 'TikTok' },
  { re: /\bLine\/[\d.]+/i,                           label: 'Line' },
  { re: /\bPinterest\b/i,                            label: 'Pinterest' },
  { re: /\bSnapchat\b/i,                             label: 'Snapchat' },
];

/**
 * Return the current audio environment assessment.
 *
 * Shape:
 *   {
 *     hostile: boolean,          // if true, force silent-highlight mode
 *     reason: string | null,     // machine key: 'in_app_webview', 'data_saver', 'no_media_element'
 *     label: string | null,      // human-friendly: 'Instagram', 'Data Saver', etc.
 *     openInBrowserHref: string, // when hostile, a link that some
 *                                // hosts respect for "open in system browser"
 *   }
 *
 * Never throws — safe to call from render paths.
 */
export function getAudioEnvironment() {
  // SSR / test-runner safety.
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { hostile: false, reason: null, label: null, openInBrowserHref: '' };
  }
  const ua = String(navigator.userAgent || '');
  const currentUrl = typeof window.location !== 'undefined' ? window.location.href : '';

  // 1. In-app WebView detection. These are the biggest offenders — a
  // share link opened inside Instagram / Facebook / X etc. often has
  // audio blocked at the OS-embed layer, not the browser layer, so no
  // amount of user-gesture priming helps.
  for (const { re, label } of IN_APP_WEBVIEW_MATCHERS) {
    if (re.test(ua)) {
      return {
        hostile: true,
        reason: 'in_app_webview',
        label,
        openInBrowserHref: currentUrl, // fallback — most in-app browsers show a "open externally" menu
      };
    }
  }

  // 2. HTMLMediaElement missing — extremely rare, but happens in some
  // headless test contexts and in a few locked-down enterprise browser
  // configurations. Downgrade rather than crash on `new Audio()` /
  // `<audio>` ref use.
  if (typeof window.HTMLMediaElement === 'undefined') {
    return {
      hostile: true,
      reason: 'no_media_element',
      label: 'Media unsupported',
      openInBrowserHref: currentUrl,
    };
  }

  // 3. Chrome / Samsung Internet "Data Saver" mode. `saveData` is
  // exposed on `navigator.connection`. When it's on, the browser
  // heavy-throttles media elements — `waitForAudioReady` typically
  // times out silently.
  try {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn && conn.saveData === true) {
      return {
        hostile: true,
        reason: 'data_saver',
        label: 'Data Saver',
        openInBrowserHref: currentUrl,
      };
    }
  } catch {
    // ignore — feature-detect only
  }

  return { hostile: false, reason: null, label: null, openInBrowserHref: '' };
}

/**
 * Convenience wrapper — one-line reason string suitable for telemetry.
 * Combines the detected reason with the raw UA for post-hoc analysis
 * in the admin logs.
 */
export function summarizeAudioEnvironment(env) {
  if (!env || !env.hostile) return 'ok';
  return `${env.reason}:${env.label || 'unknown'}`;
}
